import React from 'react';
import { GoldIcon, FeelIcon, NeedIcon, ObserveIcon, PeaceIcon } from '../icons/ElchmeIcons';
import { Sparkles } from 'lucide-react';

/**
 * ChoiceNeuroplasticityContent - Page 51 content about self-directed neuroplasticity
 * This is about the power of choice in creating our reality
 */
export default function ChoiceNeuroplasticityContent() {
  return (
    <div className="space-y-8 animate-fadeIn" data-testid="choice-neuroplasticity">
      {/* Hero Section */}
      <div className="text-center mb-8">
        <div className="w-24 h-24 bg-gradient-to-br from-amber-500 via-yellow-400 to-amber-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-amber-500/30">
          <GoldIcon size={48} />
        </div>
        <h2 className="text-3xl lg:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-yellow-200 to-amber-300 mb-4">
          The Power of Choice
        </h2>
        <p className="text-amber-200/80 text-lg">Page 51B • Self-Directed Neuroplasticity</p>
      </div>

      {/* Core Message */}
      <div className="bg-gradient-to-br from-amber-900/30 to-yellow-900/20 border border-amber-500/30 rounded-2xl p-8">
        <div className="flex items-start gap-4 mb-6">
          <Sparkles className="w-8 h-8 text-amber-400 flex-shrink-0 mt-1" />
          <div>
            <h3 className="text-xl font-bold text-amber-300 mb-3">Ultimately, This Is About Choice</h3>
            <p className="text-slate-200 leading-relaxed text-lg">
              You get a choice to determine your internal processing, your filters, your model of reality. 
              <span className="text-amber-300 font-semibold"> Permission to create your own agency around this.</span>
            </p>
          </div>
        </div>
        
        <div className="bg-slate-900/50 rounded-xl p-6 border border-amber-500/20">
          <p className="text-2xl text-center text-amber-200 font-serif italic">
            "This is self-directed neuroplasticity"
          </p>
        </div>
      </div>

      {/* Two Paths */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-slate-800/50 border border-rose-500/30 rounded-xl p-6">
          <h4 className="text-rose-400 font-bold mb-3">We can choose a model where we suffer...</h4>
          <p className="text-slate-300 text-sm">
            Reactive patterns, blame, judgment, disconnection from needs
          </p>
        </div>
        <div className="bg-slate-800/50 border border-emerald-500/30 rounded-xl p-6">
          <h4 className="text-emerald-400 font-bold mb-3">Or develop a model where we suffer less</h4>
          <p className="text-slate-300 text-sm">
            Conscious choice, connection, empathy, life-serving actions
          </p>
        </div>
      </div>

      {/* Core Purpose */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
        <h3 className="text-white font-bold text-lg mb-4 text-center">This Is All About...</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="flex items-center gap-3 p-3 bg-rose-900/20 rounded-lg">
            <FeelIcon size={24} />
            <span className="text-slate-200">Connecting at the heart level</span>
          </div>
          <div className="flex items-center gap-3 p-3 bg-emerald-900/20 rounded-lg">
            <NeedIcon size={24} />
            <span className="text-slate-200">Building lives around life-serving actions</span>
          </div>
          <div className="flex items-center gap-3 p-3 bg-blue-900/20 rounded-lg">
            <ObserveIcon size={24} />
            <span className="text-slate-200">Processing perceptions consciously</span>
          </div>
          <div className="flex items-center gap-3 p-3 bg-amber-900/20 rounded-lg">
            <GoldIcon size={24} />
            <span className="text-slate-200">Creating a reality we want to live in together</span>
          </div>
        </div>
      </div>

      {/* Application Levels */}
      <div className="bg-gradient-to-r from-purple-900/30 to-indigo-900/30 border border-purple-500/30 rounded-xl p-6">
        <h3 className="text-purple-300 font-bold text-lg mb-4">Applying This At Every Level</h3>
        <div className="space-y-3">
          {[
            { level: 'Self', desc: 'Your internal dialogue and self-compassion' },
            { level: 'Interpersonal', desc: 'One-on-one reactions and interactions' },
            { level: 'Family', desc: 'How we engage with those closest to us' },
            { level: 'Family to Family', desc: 'How families engage with other families' },
            { level: 'Global', desc: 'The potential solution to everything' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-4 p-3 bg-slate-900/50 rounded-lg">
              <span className="bg-purple-600 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center">
                {i + 1}
              </span>
              <div>
                <span className="text-purple-300 font-semibold">{item.level}:</span>
                <span className="text-slate-300 ml-2">{item.desc}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Balance */}
      <div className="bg-teal-900/20 border border-teal-500/30 rounded-xl p-6">
        <div className="flex items-center justify-center gap-3 mb-4">
          <PeaceIcon size={32} />
          <h3 className="text-teal-300 font-bold text-xl">The Balance Point</h3>
        </div>
        <p className="text-slate-200 text-center leading-relaxed">
          It's about achieving balance between <span className="text-rose-300">your needs</span> and 
          <span className="text-emerald-300"> the needs of others</span>—between 
          <span className="text-rose-300"> empathy for self</span> and 
          <span className="text-emerald-300"> empathy for others</span>.
        </p>
        <p className="text-amber-300 font-semibold text-center mt-4 text-lg">
          When all is achieved, the best outcomes result.
        </p>
      </div>

      {/* Win States */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4 text-center">
          <p className="text-red-400 font-semibold">Win by Dominance</p>
          <p className="text-slate-400 text-sm mt-1">Someone loses</p>
        </div>
        <div className="bg-slate-700/50 border border-slate-500/30 rounded-xl p-4 text-center">
          <p className="text-slate-400 font-semibold">Lose by Passivity</p>
          <p className="text-slate-500 text-sm mt-1">You lose</p>
        </div>
        <div className="bg-emerald-900/30 border border-emerald-500/30 rounded-xl p-4 text-center">
          <p className="text-emerald-400 font-bold">We All Can Win</p>
          <p className="text-emerald-300 text-sm mt-1">Following these guidelines</p>
        </div>
      </div>

      {/* What This Is NOT */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
        <h3 className="text-slate-300 font-bold mb-4 text-center">This Is NOT...</h3>
        <div className="flex flex-wrap justify-center gap-3">
          {['Dogma', 'Religion', 'Judgment'].map((item) => (
            <span key={item} className="bg-slate-700/50 text-slate-400 px-4 py-2 rounded-full text-sm">
              ✗ {item}
            </span>
          ))}
        </div>
        <p className="text-slate-300 text-center mt-4">
          It is just <span className="text-amber-300 font-semibold">one way</span> where we all can speak the same language—
          <span className="text-amber-300"> the language of belonging</span>.
        </p>
      </div>

      {/* Belonging Promise */}
      <div className="bg-gradient-to-br from-amber-900/40 to-yellow-900/30 border-2 border-amber-500/50 rounded-2xl p-8 text-center">
        <p className="text-2xl text-amber-200 font-serif leading-relaxed">
          "With belonging, we can create <span className="text-yellow-300 font-bold">perpetual abundance</span> and 
          all have the capacity to <span className="text-yellow-300 font-bold">thrive</span>."
        </p>
      </div>

      {/* Marshall's Wisdom */}
      <div className="bg-indigo-900/20 border border-indigo-500/30 rounded-xl p-6">
        <p className="text-indigo-300 font-medium mb-3">As Marshall says:</p>
        <blockquote className="text-xl text-slate-200 italic border-l-4 border-indigo-500 pl-4">
          "In some ways, no human has ever done anything for anyone..."
        </blockquote>
        <p className="text-slate-400 mt-4 text-sm">
          Meaning? We do things to meet our own needs—including the need to contribute to others' well-being.
        </p>
      </div>

      {/* Key Insights */}
      <div className="space-y-4">
        <h3 className="text-white font-bold text-lg text-center">Key Transformations</h3>
        
        <div className="bg-purple-900/20 border border-purple-500/30 rounded-xl p-4">
          <p className="text-purple-300 font-semibold">Criticism does not exist in this model</p>
          <p className="text-slate-400 text-sm mt-1">Every judgment is a tragic expression of unmet needs</p>
        </div>
        
        <div className="bg-teal-900/20 border border-teal-500/30 rounded-xl p-4">
          <p className="text-teal-300 font-semibold">Vulnerability is strength</p>
          <p className="text-slate-400 text-sm mt-1">This changes our representation of reality</p>
        </div>
        
        <div className="bg-rose-900/20 border border-rose-500/30 rounded-xl p-4">
          <p className="text-rose-300 font-semibold">Minimize non-feeling words and images that cause pain</p>
          <p className="text-slate-400 text-sm mt-1">The picture we create in our mind determines our behavior</p>
        </div>
      </div>

      {/* Final Vision */}
      <div className="bg-gradient-to-br from-rose-900/30 via-purple-900/30 to-amber-900/30 border border-amber-500/40 rounded-2xl p-8 text-center">
        <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-rose-300 via-purple-300 to-amber-300 mb-4">
          A Harmonic Coherent Convergence
        </h3>
        <div className="flex flex-wrap justify-center gap-3 mb-6">
          {['Love', 'Empathy', 'Belonging', 'Action', 'Potency'].map((word, i) => (
            <span key={word} className="bg-gradient-to-r from-rose-600/50 to-amber-600/50 text-white px-4 py-2 rounded-full font-semibold">
              {word}
            </span>
          ))}
        </div>
      </div>

      {/* Steve's Wisdom */}
      <div className="bg-emerald-900/20 border border-emerald-500/30 rounded-xl p-6">
        <p className="text-emerald-300 font-medium mb-3">As we learned from Steve:</p>
        <blockquote className="text-lg text-slate-200 italic">
          "In a way, this is all easiest to teach to a six-year-old—their mind is receptive, ready, 
          and not clouded by cultural expectations as much."
        </blockquote>
      </div>

      {/* Universal Application */}
      <div className="bg-gradient-to-r from-blue-900/30 to-teal-900/30 border border-teal-500/30 rounded-xl p-6 text-center">
        <p className="text-lg text-slate-200 leading-relaxed">
          And we can apply this again—to <span className="text-blue-300">ourselves</span>, 
          to <span className="text-rose-300">the ones we love</span>, 
          to <span className="text-amber-300">the ones we have to engage with</span>, 
          <span className="text-purple-300"> family to family</span> across the planet, 
          <span className="text-emerald-300"> species to species!</span>
        </p>
      </div>
    </div>
  );
}
